/**
 * prescribeRequirements.ts — WHAT IS STILL MISSING before a prescription can go,
 * in words, per line. Pure; shared by `PrescribeForm` and `RePrescribeModal`.
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Added 2026-09-25 (CTO: "till the
 * 'Directions' is filled it does not enable the main CTA to prescribe, but it's
 * not telling it's missed").
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * THIS FILE NAMES THE GATE. IT DOES NOT DECIDE IT.
 *
 * The rules are unchanged and still live where they did — `lineValid` in
 * PrescribeForm, `ready` in RePrescribeModal, `useSupplyRows().valid`. What is
 * here turns each failing clause into a sentence a clinician can act on, with a
 * place to jump to. The CONTRACT between the two is one-way and load-bearing:
 *
 *   whenever the gate says NO, this list is NON-EMPTY.
 *
 * An empty list under a blocked button is the defect this file exists to end
 * (a control that refuses without saying why), so every clause of the gate has
 * an item here — including ones the current UI cannot reach, like a line with
 * no valid quantity on the form that fixes it at 1. `prescribeRequired.spec.tsx`
 * walks the clauses against the gate.
 *
 * WHICH FIELDS ARE REQUIRED — checked against orders-service `origin/dev`
 * (283f2f9, 2026-09-25) as well as against the portal's own gate:
 *   · medication  — `productKey` + `medicationName`, `min(1)` in the
 *                   `prescribeRequest` schema (contracts v0.14.0).
 *   · strength    — `variantKey`; the schema tolerates its absence for the
 *                   rollout, but `ApprovalGate.assertPrescribedStrength`
 *                   refuses every new prescribe without one.
 *   · quantity    — `int().positive()`; fixed at 1 on the ordinary form, a
 *                   real field on the re-save modal.
 *   · directions  — REQUIRED END TO END. The schema's `dosage` is
 *                   `string.min(1).max(200).nullable()`: orders REJECTS an
 *                   empty string (400) and accepts only `null` — which this
 *                   portal never sends (`PrescribeForm` posts `dosage.trim()`,
 *                   the modal `directions.trim()`). So a blank Directions is a
 *                   400 on the wire as well as a refusal here. ⚠️ Making it
 *                   optional is NOT a portal-only change: it needs the portal
 *                   to send `null` for a blank box AND a clinical decision that
 *                   a prescription with no instruction to the patient is
 *                   signable. The portal has required it since the builder
 *                   existed, and the CTO's own complaint is that it is
 *                   mandatory. Marking it is not a rule change; it is the rule
 *                   the button already enforced, made visible.
 *   · supplies    — OPTIONAL as a section (`supplies: []` is valid), but a row
 *                   that is ON the list needs a whole-number quantity 1..50.
 *   · notes, repeat flag, "Add a supply" — optional; never marked.
 *
 * R28: not one medicine or supply name is written here — a supply's name is
 * passed in from the served catalogue.
 * ═════════════════════════════════════════════════════════════════════════════
 */
import type { SupplyCatalogueEntry } from '../api/types';

/**
 * The longest Directions the server takes: contracts v0.14.0 `prescribeRequest`
 * `dosage` is `string.min(1).max(200)`, and orders 400s anything longer. The
 * form shows a counter against this number and refuses — in words — a line
 * over it (2026-09-26 follow-up), rather than letting the press become a 400.
 * ⚠️ NEVER `maxLength` on the box: it truncates a paste SILENTLY, and a cut-off
 * instruction to a patient ("…once weekly for 4 wee") is worse than a refusal.
 * Measured on the TRIMMED text, which is what both surfaces post.
 */
export const MAX_DIRECTIONS_LENGTH = 200;

/** Is this Directions text within the server's limit? (Trimmed, as it is posted.) */
export function directionsWithinLimit(directions: string): boolean {
  return directions.trim().length <= MAX_DIRECTIONS_LENGTH;
}
import type { MissingItem } from '../ui/ErrorSummary';
import { MAX_SUPPLY_QUANTITY, MIN_SUPPLY_QUANTITY, supplyQuantityValid, type SupplyDraft } from './supplyRows';
import type { PrescribingContextState } from './usePrescribingContext';

/** The fields of one line the gate reads. Both surfaces' drafts fit it. */
export interface LineFacts {
  productKey: string;
  medicationName: string;
  variantKey: string;
  /** The directions — `dosage` on the wire. */
  directions: string;
  quantity: number;
}

/**
 * The element ids one line renders. `medication` / `quantity` are absent on a
 * surface that has no such field (the modal has no medication select; the
 * ordinary form fixes quantity), and `unavailable` is the outage alert the
 * ordinary form shows in place of the medication select.
 */
export interface LineIds {
  line: string;
  strength: string;
  strengthRetry: string;
  directions: string;
  medication?: string;
  quantity?: string;
  unavailable?: string;
}

/** "line 2" — every line is named, even on a one-line prescription, so no message has two readings. */
export function lineName(index: number): string {
  return `line ${index + 1}`;
}

/**
 * What one line still needs, in the order the fields sit on screen.
 *
 * `catalogueAvailable` is the ordinary form's `hasOptions`: false means the plan
 * options could not be read, there is no medication select, and the line can
 * never be completed from this screen — that is its own item, not "choose a
 * medication" from a select that is not there.
 */
export function lineRequirements(
  line: LineFacts,
  index: number,
  ids: LineIds,
  {
    catalogueAvailable = true,
    medicationEditable = true,
  }: {
    catalogueAvailable?: boolean;
    /**
     * False on the re-save modal, where the medication is the stored line's and
     * there is no control to change it — so its gate (`ready`) never reads it,
     * and neither does this list. A clause here with no clause in the gate
     * would list something the clinician cannot fix and the button ignores.
     */
    medicationEditable?: boolean;
  } = {},
): MissingItem[] {
  const name = lineName(index);
  const items: MissingItem[] = [];
  const hasMedication = !medicationEditable || (line.productKey.trim() !== '' && line.medicationName.trim() !== '');

  if (!hasMedication) {
    if (!catalogueAvailable) {
      items.push({
        key: `line-${index}-unavailable`,
        hint: `medication list (could not load)`,
        message: `The medication list could not be loaded, so ${name} cannot be prescribed — reload the page`,
        targets: [ids.unavailable, ids.line].filter((id): id is string => Boolean(id)),
      });
    } else {
      items.push({
        key: `line-${index}-medication`,
        hint: `medication (${name})`,
        message: `Choose a medication for ${name}`,
        targets: [ids.medication, ids.line].filter((id): id is string => Boolean(id)),
      });
    }
  } else if (line.variantKey.trim() === '') {
    // Only once a medication is chosen: until then there is no strength list to
    // choose from, and the idle hint under the select already says so. The
    // fallback targets cover a list that is loading (the line) or failed (its
    // Try again, whose alert explains).
    items.push({
      key: `line-${index}-strength`,
      hint: `strength (${name})`,
      message: `Choose a strength for ${name}`,
      targets: [ids.strength, ids.strengthRetry, ids.line],
    });
  }

  if (line.directions.trim() === '') {
    items.push({
      key: `line-${index}-directions`,
      hint: `directions (${name})`,
      message: `Enter directions for ${name}`,
      targets: [ids.directions, ids.line],
    });
  } else if (!directionsWithinLimit(line.directions)) {
    // The same key as the empty case: one Directions box has one error line.
    const over = line.directions.trim().length - MAX_DIRECTIONS_LENGTH;
    items.push({
      key: `line-${index}-directions`,
      hint: `shorter directions (${name})`,
      message: `Shorten the directions for ${name} to ${MAX_DIRECTIONS_LENGTH} characters or fewer (${over} over)`,
      targets: [ids.directions, ids.line],
    });
  }

  if (!Number.isInteger(line.quantity) || line.quantity < 1) {
    items.push({
      key: `line-${index}-quantity`,
      hint: `quantity (${name})`,
      message: `Enter a whole-number quantity of 1 or more for ${name}`,
      targets: [ids.quantity, ids.line].filter((id): id is string => Boolean(id)),
    });
  }

  return items;
}

/** The ids the supplies section renders — see `SuppliesSection`'s `idBase`. */
export interface SupplyIds {
  section: string;
  retry: string;
  quantity: (supplyKey: string) => string;
}

/**
 * What the supplies section still needs. Mirrors `useSupplyRows().valid`
 * clause for clause: the read has not answered (or the rows are not seeded),
 * the read failed, or a row's quantity is not a whole number in range.
 */
export function supplyRequirements(
  context: PrescribingContextState,
  rows: SupplyDraft[] | null,
  ids: SupplyIds,
): MissingItem[] {
  if (context.state === 'error') {
    return [
      {
        key: 'supplies-error',
        hint: 'supplies list (could not load)',
        message: 'The supplies list could not be loaded — retry loading supplies',
        targets: [ids.retry, ids.section],
      },
    ];
  }
  if (context.state === 'loading' || rows === null) {
    return [
      {
        key: 'supplies-loading',
        hint: 'supplies list (still loading)',
        message: 'Wait for the supplies list to finish loading',
        targets: [ids.section],
      },
    ];
  }
  const byKey = new Map<string, SupplyCatalogueEntry>(context.context.catalogue.map((entry) => [entry.supplyKey, entry]));
  return rows
    .filter((row) => !supplyQuantityValid(row.quantityText))
    .map((row) => {
      // The served name; the key only if the catalogue somehow lacks it (a row
      // the section would not render — see `usableContext`).
      const supplyName = byKey.get(row.supplyKey)?.name ?? row.supplyKey;
      return {
        key: `supply-${row.supplyKey}-quantity`,
        hint: `quantity of ${supplyName}`,
        message: `Enter a quantity from ${MIN_SUPPLY_QUANTITY} to ${MAX_SUPPLY_QUANTITY} for ${supplyName}`,
        targets: [ids.quantity(row.supplyKey), ids.section],
      };
    });
}

/**
 * The errors to SHOW: what is still missing now, restricted to what was
 * missing at the last blocked press. An error clears the moment its field is
 * filled; a field that only became empty AFTER the press (a line added since)
 * is listed in the "Still needed" line, but gets no red text until the next
 * press — shouting at a box the clinician has not reached yet is not help.
 */
export function shownErrors(missing: MissingItem[], flagged: ReadonlySet<string> | null): MissingItem[] {
  if (!flagged) return [];
  return missing.filter((item) => flagged.has(item.key));
}
